import { setShoppingLocation } from "@wbmason/main-header-api";
import { IListItemAction, INewListRequest } from "@wbmason/my-list-service";
import { AnimatePresence } from "framer-motion";
import React from "react";
import {
  forkJoin,
  fromEvent,
  Observable,
  of,
  Subject,
  Subscription,
} from "rxjs";
import {
  CartServiceAccessor,
  GetRemoveCartItemsCall,
} from "../utilities/cartServiceAccessor";
import ConfigProps from "../constants/configProps";
import { CheckOutResponseType } from "../enums/CheckOutResponseType";
import { IAddressBudget } from "../interfaces/IAddressBudget";
import { IAvailabilityInfo } from "../interfaces/IAvailabilityInfo";
import { ICartItem } from "../interfaces/ICartItem";
import { ICartTotals } from "../interfaces/ICartTotals";
import { IDeliveryNotifications } from "../interfaces/IDeliveryNotifications";
import { IDisplayState } from "../interfaces/IDisplayState";
import { IItemIndicator } from "../interfaces/IItemIndicator";
import { IOrdersAwaitingApproval } from "../interfaces/IOrdersAwaitingApproval";
import { ISessionNotifications } from "../interfaces/ISessionNotifications";
import { IShoppingLocation } from "../interfaces/IShoppingLocation";
import { ITabSetCategory } from "../interfaces/ITabSetCategory";
import { IUserBudget } from "../interfaces/IUserBudget";
import { IWorkflow } from "../interfaces/IWorkflow";
import { CartService } from "../utilities/cartService";
import ConfigPropService from "../utilities/configPropService";
import DeliveryNotificationService from "../utilities/deliveryNotificationService";
import DigitalDataService from "../utilities/digitalDataService";
import {
  bootstrap as externalApiBoostrap,
  destroy as externalApiDestroy,
  emitCartItems as externalApiEmitCartItems,
} from "../utilities/externalApiService";
import OrderApprovalService from "../utilities/orderApprovalService";
import ScrollBarService from "../utilities/scrollBarService";
import SessionCacheService from "../utilities/sessionCacheService";
import { TabSetsService } from "../utilities/tabSetsService";
import UpdateShoppingLocationService from "../utilities/updateShoppingLocationService";
import UserService from "../utilities/userService";
import { IBrandOrService } from "./brandsAndServices/brandOrService";
import CartWrapper from "./cart/cartWrapper";
import HeaderBottom from "./headerBottom";
import HeaderMain from "./headerMain";
import HeaderSafeZoneWrapper from "./HeaderSafeZoneWrapper";
import HeaderTop from "./headerTop";
import { MyListsProvider } from "./myLists/MyListsProvider";
import MyListsWrapper from "./myLists/myListsWrapper";
import ManageLists from "./myLists/manageLists";
import NavMenuWrapper from "./navMenu/navMenuWrapper";
import NavShadow from "./navMenu/navShadow";
import LiveChat from "./needHelp/liveChat";
import { NotificationsBell } from "./notifications/notificationsBell";
import NotificationsWrapper from "./notifications/notificationsWrapper";
import { AddToCartProvider } from "./addtocart/AddToCartProvider";
import { MerchZonesProvider } from "./merchZones/MerchZonesProvider";
import { ListAction } from "@wbmason/my-list-service/constants/enums";
import { CartService as WbmCartService } from "@wbmason/cart-service";
import EnvironmentService from "../utilities/environmentService";
import { IAlgoliaConfig } from "../interfaces/IAlgoliaConfig";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { SuccessAlert } from "wbm-generic-components";
import { DnBService } from "../utilities/dnbService";

const NotificationsBellCountKey = "NotificationsBellCount";

export interface IWbmMainHeaderProps {
  shoppingLocation?: IShoppingLocation;
  cartId: string;
  workflow: IWorkflow;
  cartItemsSerialized?: string;
  isApprover: boolean;
  deliveryNotifications?: IDeliveryNotifications;
  accountNumber: string;
  customerAccountName: string;
  isLoggedIn: boolean;
  isCookied: boolean;
  userConfigurations: number;
  displayState: IDisplayState;
  customLogo?: string;
  firstName: string;
  checkoutAppUri: string;
  hasItemAgreements: boolean;
  myListsUrl?: string;
  brandsAndServices: IBrandOrService[];
  userConfigId?: string;
  preLoadedLoginUrl?: string;
  preLoadedRegistrationUrl?: string;
  preLoadedSwitchAccountUrl?: string;
  searchKeyword?: string;
  enableBottomOffset?: boolean;
  searchAction?: string;
  algoliaConfig?: IAlgoliaConfig;
  visitId?: string;
}

export interface IWbmMainHeaderState {
  headerSafeZoneHeight: number;
  hasMarketingDiv: boolean;
  navMenuOpen: boolean;
  myListsOpen: boolean;
  notificationsOpen: boolean;
  cartTotals: ICartTotals;
  categories: ITabSetCategory[];
  ordersAwaitingApproval: IOrdersAwaitingApproval;
  deliveryNotifications: IDeliveryNotifications;
  deliveryNotificationsViewed: boolean;
  itemIndicators: IItemIndicator[];
  userBudget?: IUserBudget;
  myListsLeftSide: boolean;
  myListsIsSecondary: boolean;
  myListItemsToAdd: IListItemAction[] | null;
  actionType: number;
  listAction: ListAction;
  productSet: INewListRequest | null;
  shoppingLocation?: IShoppingLocation;
  canCloseNotifications: boolean;
  addressBudget?: IAddressBudget;
  availabilityInfo: IAvailabilityInfo[];
  useNotificationAnimation: boolean;
  headerSafeZoneWidth: number;
  headerOffset: number;
  forceCloseDep: boolean;
  navShadowOpen: boolean;
  preventCheckoutMessage: string;
  defaultSortOrderSearch: string;
  addToListEnabled: boolean;
  cartId: string;
  hasItemAgreements: boolean;
  hasRemoveCartError?: boolean;
  showErrorToast?: boolean;
  pageName?: string;
}

class WBMMainHeader extends React.PureComponent<
  IWbmMainHeaderProps,
  IWbmMainHeaderState
> {
  addressBudgetSubject = new Subject<IAddressBudget>();

  constructor(props: IWbmMainHeaderProps) {
    super(props);

    this.state = {
      navMenuOpen: false,
      myListsOpen: false,
      notificationsOpen: false,
      cartTotals: {} as ICartTotals,
      categories: [],
      ordersAwaitingApproval: {} as IOrdersAwaitingApproval,
      deliveryNotifications: {} as IDeliveryNotifications,
      deliveryNotificationsViewed: false,
      itemIndicators: [],
      hasMarketingDiv: false,
      myListsLeftSide: false,
      myListsIsSecondary: false,
      myListItemsToAdd: [],
      actionType: 0,
      listAction: ListAction.Add,
      productSet: null,
      shoppingLocation: this.props.shoppingLocation,
      canCloseNotifications: true,
      availabilityInfo: [],
      useNotificationAnimation: false,
      headerSafeZoneHeight: 0,
      headerSafeZoneWidth: 0,
      headerOffset: 180,
      forceCloseDep: false,
      navShadowOpen: false,
      preventCheckoutMessage: "",
      defaultSortOrderSearch: "",
      addToListEnabled: false,
      cartId: this.props.cartId,
      hasItemAgreements: this.props.hasItemAgreements,
      hasRemoveCartError: false,
      showErrorToast: false,
      pageName: "",
    };

    this.setNavMenuOpen = this.setNavMenuOpen.bind(this);
    this.setMyListsOpen = this.setMyListsOpen.bind(this);
    this.setNotificationsOpen = this.setNotificationsOpen.bind(this);
    this.setCanCloseNotifications = this.setCanCloseNotifications.bind(this);
    this.removeCartItem = this.removeCartItem.bind(this);
    this.marketingDivCallback = this.marketingDivCallback.bind(this);
    this.safeZoneCallback = this.safeZoneCallback.bind(this);
    this.setHeaderSafeZone = this.setHeaderSafeZone.bind(this);
    this.setShoppingLocation = this.setShoppingLocation.bind(this);
    this.updateShoppingCartEventListener =
      this.updateShoppingCartEventListener.bind(this);
    this.productAvailabilityCallback =
      this.productAvailabilityCallback.bind(this);
    this.megaMenuOpened = this.megaMenuOpened.bind(this);
    this.setNotificationsAnimate = this.setNotificationsAnimate.bind(this);
    this.onAddToListAction = this.onAddToListAction.bind(this);
    this.onAddToListEnabled = this.onAddToListEnabled.bind(this);
    this.onManageListAction = this.onManageListAction.bind(this);

    externalApiBoostrap();
  }

  marketingDivCallback() {
    this.setState({
      hasMarketingDiv: true,
    });
  }

  setReactHeaderOffset() {
    const headerOffsetTag = document.getElementsByClassName(
      "react-header-offset",
    )[0] as HTMLElement;

    const globalMaintenanceMessage = document.getElementsByClassName(
      "global-maintenance-message",
    )[0] as HTMLElement;

    const headerOffset = this.getHeaderHeight();

    this.setState({
      headerOffset: headerOffset,
    });

    if (headerOffsetTag) {
      headerOffsetTag.style.minHeight = headerOffset + "px";
    }

    if (globalMaintenanceMessage) {
      globalMaintenanceMessage.style.paddingTop = headerOffset + "px";
    }
  }

  safeZoneCallback(element: HTMLElement) {
    this.setReactHeaderOffset();

    this.setState({
      headerSafeZoneHeight: element.offsetHeight,
      headerSafeZoneWidth: element.offsetWidth,
    });
  }

  setHeaderSafeZone(element: HTMLElement) {
    if (
      element &&
      (element.offsetHeight !== this.state.headerSafeZoneHeight ||
        element.offsetWidth !== this.state.headerSafeZoneWidth)
    ) {
      this.setReactHeaderOffset();

      this.setState({
        headerSafeZoneHeight: element.offsetHeight,
        headerSafeZoneWidth: element.offsetWidth,
      });
    }
  }

  setOrdersAwaitingApproval() {
    const ordersAwaitingApproval = this.state.ordersAwaitingApproval;

    this.setState({
      ordersAwaitingApproval: {
        totalOrders: ordersAwaitingApproval.totalOrders - 1,
        notificationCount: ordersAwaitingApproval.notificationCount - 1,
        orders: ordersAwaitingApproval.orders,
      },
    });
  }

  getHeaderHeight() {
    const headerTopDesktop = document.getElementsByClassName(
      "header-top-desktop",
    )[0] as HTMLElement;

    const headerMainDesktop = document.getElementsByClassName(
      "header-main-desktop",
    )[0] as HTMLElement;

    const headerSafeZone = document.getElementsByClassName(
      "header-safe-zone-wrapper",
    )[0] as HTMLElement;

    const headerBottomTag = document.getElementById(
      "header-bottom",
    ) as HTMLElement;

    const headerMainMobile = document.getElementsByClassName(
      "header-main-mobile",
    )[0] as HTMLElement;

    const headerTopHeightDesktop = headerTopDesktop
      ? headerTopDesktop.offsetHeight
      : 0;
    const headerMainHeightDesktop = headerMainDesktop
      ? headerMainDesktop.offsetHeight
      : 0;
    const headerSafeZoneHeight = headerSafeZone
      ? headerSafeZone.offsetHeight
      : 0;
    const headerBottomHeight = headerBottomTag
      ? headerBottomTag.offsetHeight
      : 0;
    const headerMainHeightMobile = headerMainMobile
      ? headerMainMobile.offsetHeight
      : 0;

    const headerHeight =
      headerTopHeightDesktop +
      headerMainHeightDesktop +
      headerSafeZoneHeight +
      headerBottomHeight +
      headerMainHeightMobile;

    //20 is for the extra space between header and main page
    return this.props.enableBottomOffset === undefined ||
      this.props.enableBottomOffset === true
      ? headerHeight + 20
      : headerHeight;
  }

  parseBudget(value: string) {
    return parseFloat(value.replace("$", "").replace(/,/g, ""));
  }

  private orderApprovalSub: Subscription | undefined;
  private reduceOrderApprovalCountSub: Subscription | undefined;
  private getMoreOrderApprovalsSub: Subscription | undefined;
  private addressBudgetSub: Subscription | undefined;
  private tabSetCategoriesSub: Subscription | undefined;
  private notificationsSub: Subscription | undefined;
  private configPropSub: Subscription | undefined;
  private addToCartSubscription: Subscription | undefined;
  private hasItemAgreementsSubscription: Subscription | undefined;

  componentDidMount() {
    this.configPropSub = ConfigPropService.get([
      ConfigProps.PreventCheckoutMessage,
      ConfigProps.DefaultSortOrderSearch,
      ConfigProps.EnableDnBCall,
    ]).subscribe((configProps) => {
      this.setState({
        preventCheckoutMessage: configProps[ConfigProps.PreventCheckoutMessage],
        defaultSortOrderSearch: configProps[ConfigProps.DefaultSortOrderSearch],
      });

      DnBService.shouldCallAPI(
        !this.props.isLoggedIn && !this.props.isCookied,
        ConfigPropService.isYesOrTrue(configProps[ConfigProps.EnableDnBCall]),
        this.state.shoppingLocation?.state || "",
        this.props.visitId || "",
        this.state.shoppingLocation?.countryCode || "",
      );
    });

    this.orderApprovalSub =
      OrderApprovalService.getOrderApprovalObservable().subscribe((data) => {
        OrderApprovalService.approveRejectOrder(data).subscribe((response) => {
          if (
            response.checkOutResponseType === CheckOutResponseType.SOCreated ||
            response.checkOutResponseType === CheckOutResponseType.Rejected
          ) {
            var currentCount = parseInt(
              SessionCacheService.getValue(NotificationsBellCountKey),
            );

            SessionCacheService.set(
              NotificationsBellCountKey,
              currentCount - 1,
            );

            OrderApprovalService.completeOrder(data.approvalGuid, data);

            OrderApprovalService.orderApproveReject(data.approvalGuid);

            if (data.cartId === this.state.cartId) {
              window.location.reload();
            } else {
              OrderApprovalService.emitGetMoreOrders();
            }
          } else {
            if (
              response.checkOutResponseType ===
              CheckOutResponseType.DeliveryChargeChanged
            ) {
              OrderApprovalService.emitGetMoreOrders();
            }

            OrderApprovalService.emitApprovalError(data.approvalGuid, response);
          }
        }); // todo error handling
      });

    this.reduceOrderApprovalCountSub =
      OrderApprovalService.getReduceOrderApprovalCountObservable().subscribe(
        () => {
          this.setOrdersAwaitingApproval();
        },
      );

    this.getMoreOrderApprovalsSub =
      OrderApprovalService.getMoreOrdersObservable().subscribe(() => {
        OrderApprovalService.getOrderApprovals().subscribe(
          (ordersAwaitingApproval) => {
            var currentOrders = this.state.ordersAwaitingApproval.orders;

            if (
              this.state.ordersAwaitingApproval.orders !==
              ordersAwaitingApproval.orders
            ) {
              currentOrders = ordersAwaitingApproval.orders;
            }

            const newOrders = [];

            // keep any orders that aren't complete
            // retain their order
            for (let i = 0; i < currentOrders.length; i++) {
              if (
                !OrderApprovalService.isCompleted(
                  currentOrders[i].awaitingOrderIdentifier.approvalGuid,
                )
              ) {
                newOrders.push(currentOrders[i]);
              }
            }

            // pop in new orders recieved from the API
            // verify  none of the are orders have already been approved
            // or are already in the array
            for (let i = 0; i < ordersAwaitingApproval.orders.length; i++) {
              if (
                !OrderApprovalService.isCompleted(
                  ordersAwaitingApproval.orders[i].awaitingOrderIdentifier
                    .approvalGuid,
                ) &&
                newOrders.filter(
                  (o) =>
                    o.awaitingOrderIdentifier.approvalGuid ===
                    ordersAwaitingApproval.orders[i].awaitingOrderIdentifier
                      .approvalGuid,
                ).length === 0
              ) {
                newOrders.push(ordersAwaitingApproval.orders[i]);
              }
            }

            ordersAwaitingApproval.orders = newOrders;

            const notifications = SessionCacheService.getValue("Notifications");
            let salesId = "";

            if (ordersAwaitingApproval.orders.length > 0) {
              salesId =
                ordersAwaitingApproval.orders[0].awaitingOrderIdentifier
                  .orderNumber;
            }

            SessionCacheService.set("Notifications", {
              salesId: salesId,
              count: notifications.count,
            });

            for (let i = 0; i < ordersAwaitingApproval.orders.length; i++) {
              ordersAwaitingApproval.orders[i].retrievalTime = Date.now();
            }

            this.setState({
              ordersAwaitingApproval: ordersAwaitingApproval,
            });
          },
        );
      });

    this.addressBudgetSub = this.addressBudgetSubject
      .asObservable()
      .subscribe((addressBudget) => {
        this.setState({
          addressBudget: addressBudget,
        });
      });

    // this is an anti-pattern. All subscriptions should be disposed when a component unmounts
    // When workflow changes, all cached information must be wiped.
    const workflow = SessionCacheService.getValue("Workflow");
    // ditto for cart id
    const cartId = this.state.cartId;

    const cartChangeCookie = CartService.getCartChangeCookie();

    if (
      workflow &&
      JSON.stringify(workflow) !== JSON.stringify(this.props.workflow)
    ) {
      SessionCacheService.clear();
    }

    if (cartId !== SessionCacheService.getValue("CartId") || cartChangeCookie) {
      SessionCacheService.clear();
    }

    var cachedCartId = SessionCacheService.getValue("CartId");
    if (!cachedCartId) {
      cachedCartId = "";
    }

    if (cartId !== cachedCartId) {
      SessionCacheService.clear();
    }

    const serializedCartItems = SessionCacheService.getValue(
      "SerializedCartItems",
    );
    if (this.props.cartItemsSerialized !== serializedCartItems) {
      SessionCacheService.clear();
      SessionCacheService.set(
        "SerializedCartItems",
        this.props.cartItemsSerialized,
      );
    }

    SessionCacheService.set("CartId", this.state.cartId);

    SessionCacheService.set("Workflow", this.props.workflow);

    CartServiceAccessor()
      .getCart()
      .subscribe((cartTotals) => {
        const cartResponse: ICartTotals = {
          subTotal: cartTotals.cart.subTotal,
          numberOfItems: cartTotals.cart.items.length,
          totalAmount: cartTotals.cart.totalAmount,
          items: cartTotals.cart.items,
        };

        externalApiEmitCartItems(cartResponse);

        this.setState({
          cartTotals: cartResponse,
        });

        if (cartResponse.items.length > 0) {
          DigitalDataService.setCartId(
            this.state.cartId || cartResponse.items[0].cartId,
          );

          this.setState({
            cartId: cartResponse.items[0].cartId,
          });
        }

        SessionCacheService.get(
          "BudgetChanged",
          CartService.getUserBudget(),
          (budgetResponse: IUserBudget) => {
            if (budgetResponse) {
              budgetResponse.availableBudget = this.parseBudget(
                budgetResponse.formattedTotalBudget,
              );
              budgetResponse.remainingBudget =
                this.parseBudget(budgetResponse.formattedAvailableRemaining) -
                cartResponse.totalAmount;

              this.setState({
                userBudget: budgetResponse,
              });
            }
          },
        );

        const itemIndicatorIds = cartResponse.items.map((item) => {
          return {
            id: item.itemIdentifier.id,
            unitId: item.itemIdentifier.unitId,
            externalContractCode: item.externalContractCode,
            priceCode: item.priceCode,
          };
        });

        SessionCacheService.get(
          "ItemIndicatorsChanged",
          CartService.getItemIndicators(itemIndicatorIds),
          (indicatorResponse: IItemIndicator[]) => {
            this.setState({
              itemIndicators: indicatorResponse,
            });
          },
        );
      });

    this.tabSetCategoriesSub = TabSetsService.getCategories().subscribe(
      (response) => {
        if (response && response.length !== 0) {
          this.setState({
            categories: response,
          });
        }
      },
    );

    const observables: Record<
      string,
      Observable<IOrdersAwaitingApproval | IDeliveryNotifications>
    > = {};

    if (this.props.isApprover && UserService.displayUserInfo()) {
      observables.ordersAwaitingApproval =
        OrderApprovalService.getOrderApprovals();
    }

    observables.deliveryNotifications = DigitalDataService.userConfigId()
      ? DeliveryNotificationService.getDeliveryNotifications()
      : of({
          deliveryCount: 0,
        } as IDeliveryNotifications);

    this.notificationsSub = forkJoin(observables as any).subscribe((r) => {
      const responses = r as ISessionNotifications;
      this.setState({
        deliveryNotifications: responses.deliveryNotifications,
        ordersAwaitingApproval: responses.ordersAwaitingApproval
          ? responses.ordersAwaitingApproval
          : ({} as IOrdersAwaitingApproval),
      });

      const notifications = SessionCacheService.getValue("Notifications");

      if (notifications && notifications.count > 0) {
        let salesId = "";
        if (
          responses.ordersAwaitingApproval &&
          responses.ordersAwaitingApproval.orders.length > 0 &&
          responses.ordersAwaitingApproval.orders[0]
        ) {
          salesId =
            responses.ordersAwaitingApproval.orders[0].awaitingOrderIdentifier
              .orderNumber;
        }

        if (
          notifications.salesId !== salesId ||
          notifications.count !== responses.deliveryNotifications.deliveryCount
        ) {
        } else {
          this.setState({
            useNotificationAnimation: false,
          });
        }

        this.setSessionCacheNotifications(responses);
      } else {
        this.setSessionCacheNotifications(responses);
      }
    });

    if (typeof document !== "undefined") {
      document.body.id = "body";

      document.addEventListener(
        UpdateShoppingLocationService.shoppingLocationChangedEventName,
        this.updateShoppingCartEventListener,
      );

      document.addEventListener("megaMenuOpened", this.megaMenuOpened);
    }

    if (typeof window !== "undefined") {
      let hidden = false;
      let lastScroll =
        document.documentElement.scrollTop || document.body.scrollTop;
      fromEvent(document, "scroll", {
        passive: true,
      }).subscribe(() => {
        const scrollTop =
          document.documentElement.scrollTop || document.body.scrollTop;
        const movingUp = lastScroll > scrollTop;

        if (!hidden && scrollTop > 10 && !movingUp) {
          hidden = true;
          const event = document.createEvent("Event");
          event.initEvent("closeMegaMenu", true, true);
          document.dispatchEvent(event);

          const event1 = document.createEvent("Event");
          event1.initEvent("closeSearchBarDropdown", true, true);
          document.dispatchEvent(event1);

          const event2 = document.createEvent("Event");
          event2.initEvent("closeDepartmentDropdown", true, true);
          this.setState({
            forceCloseDep: true,
          });

          document.dispatchEvent(event2);

          const event3 = document.createEvent("Event");
          event3.initEvent("closeAccountAndPaymentDropdown", true, true);
          document.dispatchEvent(event3);
        } else if (hidden && (scrollTop <= 10 || movingUp)) {
          this.setState({
            forceCloseDep: false,
          });
          hidden = false;
        }
        lastScroll = scrollTop;
      });
    }

    this.addToCartSubscription =
      WbmCartService.getLastAddToCartResultSubject().subscribe(
        (addToCartResult) => {
          const { hasItemAgreement } = addToCartResult;
          if (hasItemAgreement) {
            this.setState({
              hasItemAgreements: true,
            });
          }
        },
      );
  }

  componentWillUnmount() {
    document.removeEventListener(
      UpdateShoppingLocationService.shoppingLocationChangedEventName,
      this.updateShoppingCartEventListener,
    );

    document.removeEventListener("megaMenuOpened", this.megaMenuOpened);

    if (this.tabSetCategoriesSub) {
      this.tabSetCategoriesSub.unsubscribe();
    }

    if (this.addressBudgetSub) {
      this.addressBudgetSub.unsubscribe();
    }

    if (this.orderApprovalSub) {
      this.orderApprovalSub.unsubscribe();
    }

    if (this.reduceOrderApprovalCountSub) {
      this.reduceOrderApprovalCountSub.unsubscribe();
    }

    if (this.getMoreOrderApprovalsSub) {
      this.getMoreOrderApprovalsSub.unsubscribe();
    }

    if (this.notificationsSub) {
      this.notificationsSub.unsubscribe();
    }

    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.addToCartSubscription) {
      this.addToCartSubscription.unsubscribe();
    }

    if (this.hasItemAgreementsSubscription) {
      this.hasItemAgreementsSubscription.unsubscribe();
    }

    externalApiDestroy();
  }

  componentDidUpdate(prevProps: any) {
    if (
      prevProps.shoppingLocation !== this.props.shoppingLocation &&
      this.props.shoppingLocation
    ) {
      this.setShoppingLocation(this.props.shoppingLocation);
    }
  }

  setNotificationsAnimate() {
    this.setState({
      useNotificationAnimation: true,
    });
  }

  megaMenuOpened(event: Event) {
    this.setState({
      navShadowOpen: (event as CustomEvent).detail.opened,
    });
  }

  setSessionCacheNotifications(notifications: ISessionNotifications) {
    let salesId = "";
    if (
      notifications.ordersAwaitingApproval &&
      notifications.ordersAwaitingApproval.orders.length > 0 &&
      notifications.ordersAwaitingApproval.orders[0]
    ) {
      salesId =
        notifications.ordersAwaitingApproval.orders[0].awaitingOrderIdentifier
          .orderNumber;
    }

    SessionCacheService.set("Notifications", {
      salesId: salesId,
      count: notifications.deliveryNotifications.deliveryCount,
    });
  }

  updateShoppingCartEventListener() {
    const shoppingLocation =
      UpdateShoppingLocationService.getShoppingLocation();
    this.setShoppingLocation(shoppingLocation);
  }

  setNavMenuOpen(value: boolean) {
    this.setState({
      navMenuOpen: value,
    });
  }

  setShoppingLocation(value: IShoppingLocation) {
    this.setState({
      shoppingLocation: value,
    });
    // emit shopping location through wbm-main-header-api
    setShoppingLocation(value);
  }

  productAvailabilityCallback(value: IAvailabilityInfo[]) {
    this.setState({
      availabilityInfo: value,
    });
  }

  setMyListsOpen(value: boolean, leftSide?: boolean, isSecondary?: boolean) {
    this.setState({ productSet: null });
    ScrollBarService.applyModalOpen(value, "lists", true);
    let newState = {
      myListsOpen: value,
      myListsLeftSide: leftSide ? true : false,
      myListsIsSecondary: isSecondary ? true : false,
    };
    if (!value) {
      const newValue = { myListItemsToAdd: [] };
      newState = { ...newState, ...newValue };
    }
    this.setState(newState);
  }

  setCanCloseNotifications(value: boolean) {
    this.setState({
      canCloseNotifications: value,
    });
  }

  setNotificationsOpen(value: boolean) {
    if (!this.state.canCloseNotifications && this.state.notificationsOpen) {
      return;
    }

    ScrollBarService.applyModalOpen(value, "notifications", true);

    const newState: Partial<IWbmMainHeaderState> = {
      notificationsOpen: value,
    };

    if (value === true) {
      // move all order approvals to complete
      // this prevents orders the user just approved/rejected from popping up again if they immediately close and reopen the slide out
      OrderApprovalService.setApproveRejectOrdersComplete();

      if (
        this.state.ordersAwaitingApproval &&
        this.state.ordersAwaitingApproval.orders
      ) {
        const filtered = this.state.ordersAwaitingApproval.orders.filter(
          (o) => {
            return !OrderApprovalService.isCompleted(
              o.awaitingOrderIdentifier.approvalGuid,
            );
          },
        );

        if (
          filtered.length !== this.state.ordersAwaitingApproval.orders.length
        ) {
          newState.ordersAwaitingApproval = this.state.ordersAwaitingApproval;
          newState.ordersAwaitingApproval.orders = filtered;
        }
      }
    }

    this.setState(newState as IWbmMainHeaderState);
  }

  getNotificationCount() {
    let count = 0;

    if (
      !this.state.deliveryNotificationsViewed &&
      this.state.deliveryNotifications &&
      typeof this.state.deliveryNotifications.deliveryCount === "number"
    ) {
      if (
        !DeliveryNotificationService.isViewed(
          this.state.deliveryNotifications.deliveryCount,
        )
      ) {
        count += this.state.deliveryNotifications.deliveryCount;
      }
    }

    if (
      this.state.ordersAwaitingApproval &&
      typeof this.state.ordersAwaitingApproval.totalOrders === "number"
    ) {
      count += this.state.ordersAwaitingApproval.totalOrders;
    }

    return count;
  }

  removeCartItem(cartItem: ICartItem) {
    if (this.state.hasRemoveCartError) {
      this.setState({ hasRemoveCartError: false });
    }
    // this is an anti-pattern. All subscriptions should be disposed when a component unmounts
    GetRemoveCartItemsCall(cartItem).subscribe(
      (response) => {
        if (EnvironmentService.get().isEcom) {
          let items = this.state.cartTotals.items;
          let cartTotals = Object.assign({}, this.state.cartTotals);
          cartTotals.totalAmount = this.parseBudget(response.formattedSubtotal);
          cartTotals.numberOfItems = response.itemCount;
          cartTotals.items = items.filter(
            (i) =>
              response.cartItems.filter(
                (ci) => ci.itemLineRecId === i.cartLineRecId.toString(),
              ).length !== 0,
          );

          this.setState({
            cartTotals: cartTotals,
          });
        }

        this.hasItemAgreements();

        DigitalDataService.removeFromCart(this.state.cartTotals, [cartItem]);

        if (typeof window.CustomEvent === "function") {
          const wbmremovecartItemEvent = document.createEvent("CustomEvent");
          wbmremovecartItemEvent.initCustomEvent(
            "wbmremovecartItem",
            true,
            true,
            {
              productId: cartItem.itemIdentifier.id,
              unitId: cartItem.itemIdentifier.unitId,
            },
          );
          document.dispatchEvent(wbmremovecartItemEvent);
        }

        const cartItemsChangedEvent = document.createEvent("Event");
        cartItemsChangedEvent.initEvent("CartItemsChanged", true, true);
        document.dispatchEvent(cartItemsChangedEvent);
      },
      () => {
        this.setState({ hasRemoveCartError: true });
        this.setState({ showErrorToast: true });
      },
    );
  }

  hasItemAgreements() {
    if (!EnvironmentService.get().isEcom) {
      this.hasItemAgreementsSubscription =
        WbmCartService.hasItemAgreements().subscribe((hasItemAgreements) => {
          this.setState({ hasItemAgreements });
        });
    }
  }

  onAddToListAction(itemsToAdd: IListItemAction[] | null, actionType: number) {
    this.setState({
      myListItemsToAdd: itemsToAdd,
    });
    this.setMyListsOpen(true);
    this.setState({ actionType });
  }

  onAddToListEnabled(enabled: boolean) {
    this.setState({
      addToListEnabled: enabled,
    });
  }

  onManageListAction(
    productSet: INewListRequest | null,
    listAction: ListAction,
    pageName?: string,
  ) {
    this.setState({ productSet, listAction, pageName });
  }

  render() {
    return (
      <div className="header-app header-app-fixed">
        <div id="slide-in-content-dom"></div>
        <MyListsProvider
          onAddToListAction={this.onAddToListAction}
          onAddToListEnabled={this.onAddToListEnabled}
          onManageListAction={this.onManageListAction}
        />
        <AnimatePresence>
          {this.state.navShadowOpen && (
            <NavShadow
              className={"nav-shadow-mega-menu"}
              onClick={() => {
                this.setState({ navShadowOpen: false });
              }}
            />
          )}
        </AnimatePresence>
        <header
          className="main-header-container container-fluid hide-from-printer"
          style={{ zIndex: 499, backgroundColor: "white" }}
          id="main-header-container-id"
        >
          <HeaderTop
            accountNumber={this.props.accountNumber}
            customerAccountName={this.props.customerAccountName}
            isLoggedIn={this.props.isLoggedIn}
            isCookied={this.props.isCookied}
            userConfigurations={this.props.userConfigurations}
            marketingDivCallback={this.marketingDivCallback}
            displayState={this.props.displayState}
            userConfigId={this.props.userConfigId}
          />

          <HeaderSafeZoneWrapper
            safeZoneCallback={this.safeZoneCallback}
            headerSafeZoneHeight={this.state.headerSafeZoneHeight}
            headerSafeZoneWidth={this.state.headerSafeZoneWidth}
            setHeaderSafeZone={this.setHeaderSafeZone}
          />

          <HeaderMain
            customLogo={this.props.customLogo}
            firstName={this.props.firstName}
            isLoggedIn={this.props.isLoggedIn}
            isCookied={this.props.isCookied}
            navMenuOpen={this.state.navMenuOpen}
            setNavMenuOpen={this.setNavMenuOpen}
            setNotificationsOpen={this.setNotificationsOpen}
            cartTotals={this.state.cartTotals}
            removeCartItem={this.removeCartItem}
            notificationCount={this.getNotificationCount()}
            useNotificationAnimation={this.state.useNotificationAnimation}
            itemIndicators={this.state.itemIndicators}
            userBudget={this.state.userBudget}
            displayState={this.props.displayState}
            hasMultipleAccounts={this.props.userConfigurations > 1}
            safeZoneHeight={this.state.headerSafeZoneHeight}
            forceCloseDep={this.state.forceCloseDep}
            accountNumber={this.props.accountNumber}
            customerAccountName={this.props.customerAccountName}
            searchKeyword={this.props.searchKeyword}
            searchAction={this.props.searchAction}
            myListsUrl={this.props.myListsUrl}
            algoliaConfig={this.props.algoliaConfig}
            setMyListsOpen={this.setMyListsOpen}
          />

          <CartWrapper
            safeZoneHeight={this.state.headerSafeZoneHeight}
            removeItemCallback={this.removeCartItem}
            cartTotals={this.state.cartTotals}
            isLoggedIn={this.props.isLoggedIn}
            isCookied={this.props.isCookied}
            itemIndicators={this.state.itemIndicators}
            userBudget={this.state.userBudget}
            hasMarketingDiv={this.state.hasMarketingDiv}
            displayState={this.props.displayState}
            shoppingLocation={this.state.shoppingLocation}
            setShoppingLocation={this.setShoppingLocation}
            workflow={this.props.workflow}
            checkoutAppUri={this.props.checkoutAppUri}
            budgetCallback={(addressBudget: IAddressBudget) => {
              this.addressBudgetSubject.next(addressBudget);
            }}
            addressBudget={this.state.addressBudget}
            productAvailabilityCallback={this.productAvailabilityCallback}
            availabilityInfo={this.state.availabilityInfo}
            hasItemAgreements={this.state.hasItemAgreements}
            cartId={this.state.cartId}
            custAccount={this.props.accountNumber}
            hasRemoveCartError={this.state.hasRemoveCartError}
          />

          <HeaderBottom
            isLoggedIn={this.props.isLoggedIn}
            isCookied={this.props.isCookied}
            setMyListsOpen={this.setMyListsOpen}
            categories={this.state.categories}
            shoppingLocation={this.state.shoppingLocation}
            setShoppingLocation={this.setShoppingLocation}
            displayState={this.props.displayState}
            workflow={this.props.workflow}
            budgetCallback={(addressBudget: IAddressBudget) => {
              this.addressBudgetSubject.next(addressBudget);
            }}
            brandsAndServices={this.props.brandsAndServices}
            myListsUrl={this.props.myListsUrl}
            defaultSortOrderSearch={this.state.defaultSortOrderSearch}
          />
        </header>
        <NavMenuWrapper
          isLoggedIn={this.props.isLoggedIn}
          isCookied={this.props.isCookied}
          firstName={this.props.firstName}
          accountNumber={this.props.accountNumber}
          customerAccountName={this.props.customerAccountName}
          isOpen={this.state.navMenuOpen}
          setOpen={this.setNavMenuOpen}
          userConfigurations={this.props.userConfigurations}
          categories={this.state.categories}
          brandsAndServices={this.props.brandsAndServices}
          isMyListsOpen={this.state.myListsOpen}
          setMyListsOpen={this.setMyListsOpen}
          displayState={this.props.displayState}
          myListsUrl={this.props.myListsUrl}
          userConfigId={this.props.userConfigId}
        />
        <AnimatePresence>
          {this.state.myListsOpen && (
            <MyListsWrapper
              closeRight={this.state.myListsLeftSide ? undefined : true}
              isSecondary={this.state.myListsIsSecondary}
              key="mylists"
              setOpen={this.setMyListsOpen}
              isLoggedIn={this.props.isLoggedIn}
              isCookied={this.props.isCookied}
              itemsToAdd={this.state.myListItemsToAdd}
              actionType={this.state.actionType}
              addToListEnabled={this.state.addToListEnabled}
            />
          )}
          {this.state.myListsOpen && !this.state.navMenuOpen && (
            <NavShadow
              onClick={() => {
                this.setMyListsOpen(false);
              }}
            />
          )}
          <ManageLists
            addToListEnabled={this.state.addToListEnabled}
            isLoggedIn={this.props.isLoggedIn}
            isCookied={this.props.isCookied}
            productSet={this.state.productSet}
            listAction={this.state.listAction}
            pageName={this.state.pageName}
          />
        </AnimatePresence>
        <AnimatePresence>
          {this.state.notificationsOpen && (
            <NotificationsWrapper
              closeRight
              key="notifications"
              setOpen={this.setNotificationsOpen}
              isApprover={this.props.isApprover}
              ordersAwaitingApproval={this.state.ordersAwaitingApproval}
              isCookied={this.props.isCookied}
              preventCheckoutMessage={this.state.preventCheckoutMessage}
            />
          )}
          {this.state.notificationsOpen && (
            <NavShadow
              onClick={() => {
                this.setNotificationsOpen(false);
              }}
            />
          )}
        </AnimatePresence>
        <LiveChat />
        <NotificationsBell
          animateBellCallback={this.setNotificationsAnimate}
          deliveryNotifications={this.state.deliveryNotifications}
          ordersAwaitingApproval={this.state.ordersAwaitingApproval}
        />
        <AddToCartProvider
          hasItemAgreements={this.state.hasItemAgreements}
          checkoutAppUri={this.props.checkoutAppUri}
          isLoggedIn={this.props.isLoggedIn}
          preLoadedLoginUrl={this.props.preLoadedLoginUrl!}
        />
        <MerchZonesProvider />
        {this.state.showErrorToast && (
          <SuccessAlert
            timerLengthInMS={3500}
            danger
            dangerText="Please try again. If the problem persists, please call 1-888-WB-MASON."
            dangerAttentionText="An error has occurred!"
            onClickClose={() => this.setState({ showErrorToast: false })}
          />
        )}
      </div>
    );
  }
}

export default WBMMainHeader;
